<template>
  <b-toast solid
           auto-hide-delay="30000"
           :toast-class="toastClass"
           :header-class="headerClass"
           :toaster="position"
           :no-auto-hide="noAutoHide"
           :id="id"
           :to="link"
           v-show="title.length > 0"
           @hide="clearDateTimeInterval"
           @hidden="onHidden"
           @show="onShow"
           @shown="autoClose">
    <button class="btn btn-sm text-white text-xxs2 bg-blue-60-opaque border-full-rounded position-absolute call-fishing-clear-queues"
            v-if="queueCount > 1"
            @click="clearNotificationQueue">
      Clear All
    </button>
    <div class="notification-body-wrapper"
         @click="onNotificationClick">
      <div class="d-flex flex-row align-items-center">
        <b-badge v-if="id === 'callFishing' && queueCount > 1"
                 class="call-fishing-queue-badge d-flex justify-center align-items-center position-absolute"
                 variant="danger"
                 pill>
          {{ queueCount }}
        </b-badge>
        <div class="notification-icon"
             :class="notificationIconClasses"
             @click="toInbox">
          <system-update-icon v-if="id === 'system'"/>
          <sms-icon v-if="id === 'sms'"/>
          <call-icon v-if="id === 'call'"/>
          <voicemail-icon v-if="id === 'voicemail'"/>
          <mention-icon v-if="id === 'mention'"/>
          <reminder-icon v-if="id === 'reminder'"/>
          <call-incoming-icon v-if="isCall"/>
        </div>
        <div class="notification-details"
             :class="[(!['incomingCall','callFishing'].includes(id) ? 'w-100' : 'flex-grow-1'), (id === 'callFishing' && queue ? 'pl-2' : '')]"
             @click="toInbox">
          <div class="d-flex flex-grow-1 align-items-baseline w-100"
               v-if="getSource">
            <span class="mr-auto text-white pr-1 text-sm">
              {{ getSource }}
            </span>
          </div>
          <div class="d-flex flex-grow-1 align-items-baseline w-100">
            <!--b-img blank blank-color="#ff5555" class="mr-2" width="12" height="12"></b-img-->
            <strong class="mr-auto text-white title pr-1">
              {{ title }}
            </strong>
            <small class="mr-2 text-grey-82 time text-nowrap"
                   v-if="!isCall">
              {{ runningDateTime }}
            </small>
          </div>

          <!-- caller location -->
          <div class="d-flex flex-grow-1 align-items-baseline w-100"
               v-if="location">
            <span class="mr-auto text-white pr-1 text-sm">
              {{ location }}
            </span>
          </div>

          <div class="text-grey-81 message-body text-break d-flex w-100">
            <div class="flex-grow-1 d-flex align-items-center w-100">
              <component class="message-icon mr-1"
                         :is="messageIcon"
                         v-if="messageIcon"/>
              <span v-if="!isCall && message"
                    class="message-text"
                    v-html="$options.filters.sanitizeHtml($options.filters.nl2br(message, false))">
              </span>
              <span v-if="id === 'sms' && attachment && !message"
                    class="message-text">
                Inbound MMS
              </span>
              <div class="message-text row has-ring-group"
                   v-else-if="isCall && campaignName && ringGroupName">
                <div class="campaign-wrapper col-5">
                  <div class="campaign-name">{{ campaignName }}</div>
                </div>
                <div class="flex-grow-1">></div>
                <div class="ring-group-wrapper col-5">
                  <div class="ring-group-name">{{ ringGroupName }}</div>
                </div>
              </div>
              <span class="message-text d-flex"
                    v-else-if="isCall && campaignName && !ringGroupName">
                {{ campaignName }}
              </span>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center align-items-center call-actions"
             v-if="shouldShowCallActions">
          <q-btn class="height-32 mr-2"
                 ripple
                 round
                 no-caps
                 @click="rejectCall">
            <cancel-call-icon width="32"
                              height="32"
                              v-if="id === 'incomingCall'"/>
            <ignore-call-icon v-if="id === 'callFishing'"/>
            <q-tooltip anchor="top middle"
                       self="center middle">
              {{ id === 'incomingCall' ? 'Decline' : tooltipMessage }}
            </q-tooltip>
          </q-btn>
          <q-btn class="height-32"
                 ripple
                 round
                 no-caps
                 @click="answerCall">
            <q-tooltip anchor="top middle"
                       self="center middle">
              Answer
            </q-tooltip>
            <accept-call-icon width="32" height="32"/>
          </q-btn>
        </div>
        <div class="d-flex justify-content-center align-items-center call-fishing-actions"
             :class="id === 'callFishing' && getSource ? 'mt-2' : ''"
             v-if="shouldShowFishingActions">
          <q-btn class="height-32 mr-2"
                 ripple
                 round
                 no-caps
                 @click="ignoreFishing">
            <ignore-call-icon/>
            <q-tooltip anchor="top middle"
                       self="center middle"
                       v-if="tooltipMessage">
              {{ tooltipMessage }}
            </q-tooltip>
          </q-btn>

          <q-btn class="height-32"
                 ripple
                 round
                 no-caps
                 @click="answerCall"
                 v-if="dialer.currentStatus === 'WRAP_UP'">
            <q-tooltip anchor="top middle"
                       self="center middle">
              Answer
            </q-tooltip>
            <accept-call-icon width="32" height="32"/>
          </q-btn>

          <q-btn class="height-32"
                 ripple
                 no-caps
                 @click="answerCall"
                 v-if="dialer.currentStatus !== 'WRAP_UP' && agentStatus === AgentStatus.AGENT_STATUS_RINGING">
            <q-tooltip anchor="top middle"
                       self="center middle">
              Answer
            </q-tooltip>
            <accept-call-icon width="32" height="32"/>
          </q-btn>

          <b-dropdown no-caret
                      :right="$q.screen.lt.lg"
                      :dropright="!$q.screen.lt.lg"
                      variant="transparent"
                      class="m-2 b-compact-dropdown-button text-bold height-32"
                      v-else-if="dialer.currentStatus !== 'WRAP_UP'">
            <template #button-content>
              <accept-call-icon width="32" height="32"/>
            </template>
            <b-dropdown-item href=""
                             link-class="d-flex align-items-center"
                             @click="handleAnswerCommunication(true, false)">
              <park-call-icon class="icon-margin"
                              width="13"
                              height="13"
                              color="#9B51E0"/>Park Current Call & Connect
            </b-dropdown-item>
            <b-dropdown-item href=""
                             @click="handleAnswerCommunication(false, true)">
              <hangup-icon class="icon-margin" width="13"/>Hangup Current Call & Connect
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>
  </b-toast>
</template>

<script>
import AcceptCallIcon from 'components/icons/accept-call-icon'
import CancelCallIcon from 'components/icons/cancel-call-icon'
import HangupIcon from 'components/icons/hangup-icon'
import IgnoreCallIcon from 'components/icons/ignore-call-icon'
import ParkCallIcon from 'components/icons/park-call-icon'
import { get, isEmpty } from 'lodash'
import * as CommunicationSourceCallTypes from 'src/constants/communication-call-source-types'
import { getQueryString } from 'src/plugins/helpers/functions'
import {
  TeamInboxMixin,
  aclMixin,
  agentMixin,
  liveCallsMixin,
  mentionsMixin,
  notificationMixin,
  notificationQueueMixin,
  userMixin,
  visibilityMixin
} from 'src/plugins/mixins'
import { UNTHREADED } from 'src/store/teaminbox/teaminbox.store'
import { mapActions, mapState } from 'vuex'
import * as AgentStatus from '../constants/agent-status'
import talk2Api from 'src/plugins/api/api'
import * as CommunicationCurrentStatus from 'src/constants/communication-current-status'

export default {
  name: 'action-notification',

  mixins: [
    notificationMixin,
    notificationQueueMixin,
    mentionsMixin,
    visibilityMixin,
    aclMixin,
    agentMixin,
    userMixin,
    TeamInboxMixin,
    liveCallsMixin
  ],

  components: {
    IgnoreCallIcon,
    HangupIcon,
    AcceptCallIcon,
    CancelCallIcon,
    ParkCallIcon
  },

  props: {
    id: {
      required: false,
      type: String,
      default: 'system'
    },

    position: {
      required: false,
      type: String,
      default: 'b-toaster-bottom-right'
    }
  },

  data () {
    return {
      runningDateTime: null,
      runningDateTimeInterval: null,
      notificationListeners: {},
      AgentStatus,
      teamInboxLink: null
    }
  },

  computed: {
    ...mapState(['notifications', 'dialer', 'callFishingQueue', 'users', 'ringGroups']),
    ...mapState('cache', ['currentCompany']),

    isCall () {
      return ['incomingCall', 'callFishing'].includes(this.id)
    },

    toastClass () {
      const toastClass = { data: 'action-notification notification-border-round' }

      if (!this.isCall) {
        toastClass.data += ' bg-grey-80'
      }

      if (this.isCall) {
        toastClass.data += ' bg-blue-60-opaque position-relative background-blur incoming-call-notification'
      }

      if (this.queue) {
        toastClass.data += ' has-clear-queues'
      }

      return toastClass.data
    },

    headerClass () {
      const headerClass = { data: 'border-0 p-0' }

      if (!this.isCall) {
        headerClass.data += ' bg-grey-80'
      }

      if (this.isCall) {
        headerClass.data += ' bg-blue-60-opaque'
      }

      return headerClass.data
    },

    queue () {
      return get(this.notifications, `${this.id}.queue`, null)
    },

    message () {
      const message = get(this.notifications[this.id], 'message', '')

      return this.parseMentionToView(message)
    },

    messageIcon () {
      return get(this.notifications[this.id], 'messageIcon', null)
    },

    title () {
      const title = get(this.notifications[this.id], 'title', '')

      const fixedTitle = this.$options.filters.fixPhone(title)

      if (!['sms', 'call'].includes(this.id) || !fixedTitle) {
        return title
      }

      return fixedTitle
    },

    dateTime () {
      return get(this.notifications[this.id], 'dateTime', '')
    },

    contactId () {
      return get(this.notifications[this.id], 'contactId', '')
    },

    communicationId () {
      return get(this.notifications[this.id], 'communicationId', '')
    },

    link () {
      if (['system', 'incomingCall', 'callFishing'].includes(this.id)) {
        return null
      }

      // reminders created inside a team inbox carry a ring group — route there
      // when the user has access. otherwise open the contact: a reminder is an
      // engagement and isn't individually resolvable by the communication route.
      if (this.id === 'reminder') {
        if (this.ringGroupId && this.ringGroupId !== '' && this.teamInboxLink) {
          return {
            path: this.teamInboxLink.path
          }
        }

        return {
          path: `/contacts/${this.contactId}`
        }
      }

      const contactPath = `/contacts/${this.contactId}/communications/${this.communicationId}`

      // If the communication has a ring group id, check teamInboxLink
      if (this.ringGroupId && this.ringGroupId !== '') {
        if (this.teamInboxLink) {
          const queryString = getQueryString(this.$route.query)
          const communicationRouteId = this.viewMode === UNTHREADED ? `/${this.communicationId}` : ''

          return {
            path: `${this.teamInboxLink.path}${communicationRouteId}${queryString}`
          }
        }

        // route mentions to contacts page if no teamInboxLink is provided
        if (this.id === 'mention') {
          return {
            path: contactPath
          }
        }

        return {
          path: contactPath
        }
      }

      // route mentions to contacts page
      if (this.id === 'mention') {
        return {
          path: contactPath
        }
      }

      // If no ring group id, use regular communication page
      return {
        path: `/contacts/${this.contactId}/communications/${this.communicationId}`
      }
    },

    attachment () {
      return get(this.notifications[this.id], 'attachment', '')
    },

    noAutoHide () {
      return ['system', 'incomingCall', 'callFishing'].includes(this.id)
    },

    campaignId () {
      return get(this.notifications[this.id], 'campaignId', null)
    },

    campaignName () {
      return get(this.notifications[this.id], 'campaignName', null)
    },

    ringGroupId () {
      return get(this.notifications[this.id], 'ringGroupId', null)
    },

    ringGroupName () {
      return get(this.notifications[this.id], 'ringGroupName', null)
    },

    phoneNumber () {
      return get(this.notifications[this.id], 'phoneNumber', null)
    },

    communication () {
      return get(this.notifications[this.id], 'communication', null)
    },

    contact () {
      return get(this.notifications[this.id], 'contact', null)
    },

    isValidPhoneShowInfo () {
      const dialerCommunicationId = get(this.dialer, 'communication.id', null)
      const callFishingCommunicationId = get(this.dialer, 'callFishing.communication', null)

      return (
        (this.id === 'incomingCall' && this.communicationId === dialerCommunicationId) || (this.id === 'callFishing' && !this.dialer.call && !callFishingCommunicationId)) &&
        !this.dialer.parkedCall
    },

    queueCount () {
      if (isEmpty(this.queue)) {
        return 1
      }

      return this.queue.length + 1
    },

    getSource () {
      if (!this.communication) {
        return ''
      }

      if (this.isColdTransferUser) {
        return 'Transfer to User'
      }

      if (this.isColdTransferRg) {
        return 'Transfer to Inbox'
      }

      if (this.isIntroduceToUser) {
        return 'Introduce to User'
      }

      if (this.isIntroduceToRg) {
        return 'Introduce to Inbox'
      }

      if (this.isAddToUser) {
        return 'Add to User'
      }

      if (this.isAddToRg) {
        return 'Add to Inbox'
      }

      if (this.isSequence) {
        return 'Call from Sequence'
      }

      if (this.isCallWaiting) {
        return 'Call Waiting'
      }

      if (this.isFishingMode) {
        return 'Fishing Mode'
      }

      return 'New Inbound Call'
    },

    isSequence () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.workflow_id
    },

    isIntroduceToRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG
    },

    isIntroduceToUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER
    },

    isAddToRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return !this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG
    },

    isAddToUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return !this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER
    },

    callIsIntroduced () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.is_introduce
    },

    isColdTransferRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_COLD_RG
    },

    isColdTransferUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_COLD_USER
    },

    notificationIconClasses () {
      return [
        this.id === 'system' ? 'system-update' : ''
      ]
    },

    location () {
      if (!this.communication || !this.contact) {
        return false
      }

      let city = this.communication.city || this.contact.cnam_city || null
      const state = this.communication.state || this.contact.cnam_state || null
      const country = this.communication.country || this.contact.cnam_country || null

      if (!city && !state && !country) {
        return 'Unknown Location'
      }

      return `${city || ''}${city && state ? ', ' : ''}${state || ''}${state && country ? ' - ' : ''} ${country || ''}`
    },

    notificationQueue () {
      return this.notifications?.[this.id]?.queue
    },

    isAgentOrDialerOnCall () {
      return this.dialer.call || this.isAgentOnCall
    },

    shouldShowCallActions () {
      return this.id === 'incomingCall' || (this.id === 'callFishing' && this.dialer && !this.isAgentOrDialerOnCall)
    },

    shouldShowFishingActions () {
      return this.id === 'callFishing' && this.dialer && this.isAgentOrDialerOnCall
    },

    tooltipMessage () {
      if (!this.communication) {
        return ''
      }
      return this.isPersonalInbox ? 'Reject' : 'Ignore'
    },

    isMention () {
      return this.id === 'mention'
    }
  },

  created () {
    this.notificationListeners[this.id] = {
      updateCommunication: (communication) => {
        this.removeQueuedNotification(communication.id, communication.disposition_status2, communication.current_status2)

        if (!this.checkCommunicationMatchesUserAccessibility(communication)) {
          return
        }

        const isCallNotInProgressOrIncoming = this.isCallNotInProgressOrIncoming(communication.disposition_status2, communication.current_status2)

        if (isCallNotInProgressOrIncoming && this.isCommunicationInCallFishingQueue(communication.id)) {
          this.removeFromCallFishingQueue(communication.id)
        }

        const isAddOrIntroduceOperation = (communication.is_introduce ||
          communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER ||
          communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG) &&
          communication.legc_uuid &&
          [CommunicationCurrentStatus.CURRENT_STATUS_GREETING_NEW, CommunicationCurrentStatus.CURRENT_STATUS_RINGING_NEW].includes(communication.legc_status)

        if (isCallNotInProgressOrIncoming && this.communicationId === communication.id && !isAddOrIntroduceOperation) {
          this.processRemoveFromNotification(communication)
        }
      }
    }
  },

  mounted () {
    // Initialize teamInboxLink if ringGroupId is available on mount
    if (this.ringGroupId && this.ringGroupId !== '') {
      this.updateTeamInboxLink(this.ringGroupId)
    }
  },

  methods: {
    ...mapActions([
      'setNotifications',
      'setShowPhone',
      'clearCallFishingQueue',
      'removeFromCallFishingQueue'
    ]),

    async updateTeamInboxLink (ringGroupId) {
      if (this.isMention || await this.checkInboxAccess(ringGroupId)) {
        this.teamInboxLink = {
          path: `/team-inboxes/${ringGroupId}/contacts/${this.contactId}/communications`
        }
      } else {
        this.teamInboxLink = null
      }
    },

    startNotificationListeners () {
      this.$VueEvent.listen('update_communication', this.notificationListeners[this.id].updateCommunication)
    },

    stopNotificationListeners () {
      this.$VueEvent.stop('update_communication', this.notificationListeners[this.id].updateCommunication)
    },

    onShow () {
      console.log('Communication when event Show notification triggers: ', this.communication)
      this.stopNotificationListeners()
      this.startNotificationListeners()
    },

    autoClose () {
      console.log('Communication when event Auto close notification triggers: ', this.communication)
      this.runDateTimeInterval()

      if ((this.id === 'callFishing' && document.getElementById('callFishing') &&
          !this.isCommunicationInCallFishingQueue()) ||
        (this.id === 'incomingCall' && document.getElementById('incomingCall') &&
          isEmpty(this.dialer.call))
      ) {
        this.processRemoveFromNotification(this.communication)
        return
      }

      if (this.id === 'incomingCall' &&
        (['CALL_CONNECTED', 'INVITE_CANCELLED', 'READY'].includes(this.dialer.currentStatus))) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      if (!this.noAutoHide &&
        this.dateTime &&
        this.dateTime.diff(this.$moment(), 'seconds') <= -30) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      if (!this.title) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      const ringGroup = this.ringGroups.find(ringGroup => ringGroup.id === this.ringGroupId)
      const isPersonalInbox = ringGroup?.is_personal_inbox

      const shouldPlayFishingNotificationSound = this.id === 'callFishing' &&
        ((!isPersonalInbox && this.currentCompany.fishing_mode_notification_sound) || (isPersonalInbox && !this.isAgentOrDialerOnCall))

      // missed call / voicemail notifications use id='call' — no ding per TEL-933
      if (this.id !== 'call') {
        this.playAudio(shouldPlayFishingNotificationSound)
      }
    },

    runDateTimeInterval () {
      if (this.dateTime) {
        this.runningDateTime = this.$options.filters.shortDateTimePassed(this.dateTime, false)
        this.runningDateTimeInterval = setInterval(() => {
          this.runningDateTime = this.$options.filters.shortDateTimePassed(this.dateTime, false)
        }, 60000)
      }
    },

    clearDateTimeInterval () {
      clearInterval(this.runningDateTimeInterval)
    },

    onHidden () {
      console.log('Communication when event Hidden notification triggers: ', this.communication)
      if (this.id === 'call') {
        return
      }

      this.clearDateTimeInterval()
      this.stopNotificationListeners()

      if (this.id !== 'callFishing' ||
        (this.id === 'callFishing' &&
          !document.getElementById('callFishing'))
      ) {
        this.removeFromCallFishingNotificationQueue(this.notifications[this.id].communicationId)
        this.setNotifications({
          type: this.id,
          data: {
            title: '',
            message: '',
            messageIcon: null,
            attachment: null,
            dateTime: null,
            contactId: '',
            communicationId: '',
            campaignId: '',
            campaignName: '',
            ringGroupName: '',
            phoneNumber: '',
            communication: null,
            contact: null,
            queue: null
          }
        })
      }

      // the slot is free now — surface the next queued reminder, if any.
      // deferred: BToast's afterLeave hook sets doRender = false right after
      // emitting hidden, so a synchronous re-show from inside this handler
      // gets clobbered and the toast never renders again this session
      if (this.id === 'reminder') {
        this.$nextTick(() => {
          this.$drainReminderNotifications()
        })
      }
    },

    type () {
      switch (this.id) {
        case 'sms':
          return 'messages'
        case 'call':
        case 'callVoicemail':
          return 'calls'
        case 'voicemail':
          return 'voicemails'
        case 'mention':
          return 'mentions'
      }
      return 'sms'
    },

    answerCall () {
      if (this.dialer.currentStatus === 'WRAP_UP') {
        this.$VueEvent.fire('endWrapUp')
      }

      if (this.id === 'callFishing') {
        this.handleAnswerCommunication()
        this.closeCallNotifications(this.id, this.communicationId)
        return
      }

      this.$VueEvent.fire('answerCall')
      this.setShowPhone(true)
    },

    async ignoreFishing () {
      const ringGroup = this.ringGroups.find(ringGroup => ringGroup.id === this.ringGroupId)

      if (ringGroup?.is_personal_inbox) {
        try {
          // Force terminate for personal inboxes, since there is no other agents to take the call
          await talk2Api.V1.communication.agentForceTerminate(this.communication.id, { reject: true })
        } catch (error) {
          console.error('Failed to force terminate communication on personal inbox:', error)
        }
      }

      this.$closeActionNotification('callFishing')
      // console.log('[Action 1] Communication when event closeCallNotifications : ', this.communication)
      this.closeCallNotifications(this.id, this.communicationId)
    },

    async handleAnswerCommunication (shouldPark = false, shouldHangup = false) {
      await this.answerCommunication(shouldPark, shouldHangup)
      this.$closeActionNotification('callFishing')
    },

    async rejectCall () {
      const isCallFishing = this.id === 'callFishing'
      const ringGroup = this.ringGroups.find(ringGroup => ringGroup.id === this.ringGroupId)

      if (isCallFishing && ringGroup?.is_personal_inbox) {
        await this.ignoreFishing()
      }

      if (!isCallFishing ||
        (isCallFishing &&
          (!this.queue ||
            (this.queue && !this.queue.length))
        )
      ) {
        // console.log('[Action 2] Communication when event closeCallNotifications : ', this.communication)
        this.closeCallNotifications(this.id, this.communicationId, true)
      }

      this.$VueEvent.fire('rejectCall')

      if (isCallFishing) {
        this.$VueEvent.fire('hidePhone')

        if (this.queue && this.queue.length) {
          this.switchCallFishingFromQueue()
        } else {
          this.removeFromCallFishingQueue(this.communicationId)
        }
      }
    },

    onNotificationClick (event) {
      // check if there are call buttons in the notification
      let found = document.querySelector('.notification-body-wrapper .call-actions')
      found = !found ? document.querySelector('.notification-body-wrapper .call-fishing-actions') : found

      if (!found &&
        this.id === 'system') {
        this.$closeActionNotification(this.id)
      }
    },

    clearNotificationQueue () {
      this.setNotifications({
        type: this.id,
        data: {
          queue: null
        }
      })

      this.clearCallFishingQueue()
      this.$closeActionNotification(this.id)
    },

    toInbox (event) {
      if (this.isValidPhoneShowInfo) {
        return
      }

      let found = document.querySelector('.notification-body-wrapper .notification-icon:not(.system-update)')

      if (!found) {
        return
      }

      // Get the path from the link computed property
      const linkPath = this.link?.path
      if (linkPath && this.$route.path !== linkPath) {
        this.$router.push({ path: linkPath })
      }
    },

    toContact () {
      if (!this.isCall) {
        return
      }

      if (this.isValidPhoneShowInfo) {
        return
      }

      const className = { data: null }
      const found = event.path.find((item) => {
        className.data = get(item, 'className', null)
        return className.data && typeof className.data === 'string' && (className.data.includes('notification-details'))
      })

      if (!found) {
        return
      }

      if (this.$route.path !== `/contacts/${this.contactId}`) {
        this.$router.push({
          path: `/contacts/${this.contactId}`
        })
      }
    }
  },

  watch: {
    ringGroupId: {
      immediate: true,
      handler: function (newRingGroupId) {
        if (newRingGroupId && newRingGroupId !== '') {
          this.updateTeamInboxLink(newRingGroupId)
        } else {
          this.teamInboxLink = null
        }
      }
    },
    contactId: {
      immediate: true,
      handler: function (contactId) {
        if (contactId && this.ringGroupId) {
          this.updateTeamInboxLink(this.ringGroupId)
        }
      }
    },
    notificationQueue: {
      deep: true,
      handler: function (newValue, oldValue) {
        if (!newValue) {
          return
        }

        const difference = oldValue?.filter(queue => !newValue.includes(queue))
        const communication = difference?.[0]?.communication

        if (communication) {
          this.removeQueuedNotification(communication.id, communication.disposition_status2, communication.current_status2)
        }
      }
    }
  },

  beforeDestroy () {
    this.clearDateTimeInterval()
    this.stopNotificationListeners()
  }
}
</script>
